import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import FreeConsultWidget from "../components/FreeConsultWidget.jsx";

/**
 * LegalPage · /legal
 * Free forever · plain-english legal drafting helper.
 * Not a lawyer, does not create attorney-client relationship, does not
 * appear in court. Tells you when to see a real lawyer.
 */
export default function LegalPage() {
  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="legal-page">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">FREE FOREVER · SAGE LEGAL</div>
        <div className="flex items-start gap-4 mb-4">
          <div className="text-sage-accent shrink-0"><ProductMark slug="legal" size={48} /></div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase">Legal</h1>
        </div>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-10 sage-prose">
          Plain-english legal help. Free forever. Letter drafts, notice templates, form guides, and the honest answer to "do I need a lawyer for this?" Sage is not a lawyer, does not represent you, and will tell you clearly when a real attorney is the right next step.
        </p>

        <div className="border border-sage-warn p-4 mb-10 bg-sage-surface" data-testid="legal-disclaimer-banner">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-warn font-bold mb-2">Read this first</div>
          <div className="text-sm text-sage-text sage-prose leading-relaxed">
            Sage Legal is not a law firm. Using it does not create an attorney-client relationship. Nothing here is legal advice. If your matter is time-critical, involves criminal exposure, court deadlines, custody, or a signature you can't take back — see a licensed attorney in your jurisdiction. Legal aid options are listed at the bottom of this page.
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="border border-sage-border p-4" data-testid="legal-feature-1">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Letters + notices</div>
            <div className="text-xs text-sage-muted sage-prose">Demand letters, cease-and-desist, tenant repair notices, wage-claim letters, HOA disputes. Drafts you can send yourself.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="legal-feature-2">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Small-claims guide</div>
            <div className="text-xs text-sage-muted sage-prose">How to file, what to bring, what the judge wants to see. State-specific pointers to your county's forms.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="legal-feature-3">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Contract read-through</div>
            <div className="text-xs text-sage-muted sage-prose">Paste an agreement, get a plain-english summary and a list of the terms most likely to bite you later.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="legal-feature-4">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">"Do I need a lawyer?"</div>
            <div className="text-xs text-sage-muted sage-prose">Honest triage. Sage points you to legal aid, a bar-referral service, or a specific practice area — never at itself.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="legal-feature-5">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Fee-waiver + in-forma-pauperis</div>
            <div className="text-xs text-sage-muted sage-prose">Court fee-waiver language and the forms most states use, if paying to file is a barrier.</div>
          </div>
          <div className="border border-sage-border p-4" data-testid="legal-feature-6">
            <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">Consumer + tenant rights</div>
            <div className="text-xs text-sage-muted sage-prose">The plain-english version of your rights under federal law and common state protections.</div>
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-2xl font-bold uppercase tracking-tight text-sage-text mb-3">Ask Legal — anonymous</h2>
          <FreeConsultWidget
            role="legal-plain-english"
            productSlug="legal"
            placeholder="Describe your situation (e.g. 'my landlord hasn't returned my security deposit', 'I got a demand letter from a contractor', 'I want to write a cease-and-desist'). Include your state."
            ctaLabel="Ask Legal"
            disclaimer="Sage Legal is not a lawyer. Not legal advice. Not attorney-client. Time-critical or high-stakes matters: see a licensed attorney. Legal aid links below."
          />
        </div>

        <div className="border border-sage-border p-6 mb-10" data-testid="legal-aid-directory">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">When you need a real lawyer — free / low-cost options</div>
          <ul className="text-sm text-sage-text space-y-2 sage-prose">
            <li><a href="https://www.lsc.gov/about-lsc/what-legal-aid/get-legal-help" target="_blank" rel="noopener noreferrer" className="text-sage-accent hover:underline">Legal Services Corporation directory</a> · federally funded local legal aid</li>
            <li><a href="https://www.lawhelp.org/" target="_blank" rel="noopener noreferrer" className="text-sage-accent hover:underline">LawHelp.org</a> · free civil legal help by state</li>
            <li><a href="https://www.americanbar.org/groups/legal_services/flh-home/" target="_blank" rel="noopener noreferrer" className="text-sage-accent hover:underline">ABA Free Legal Answers</a> · online Q&amp;A with volunteer lawyers</li>
            <li><a href="https://www.probono.net/" target="_blank" rel="noopener noreferrer" className="text-sage-accent hover:underline">Pro Bono Net</a> · pro-bono programs by state</li>
            <li><a href="https://www.findhelp.org/" target="_blank" rel="noopener noreferrer" className="text-sage-accent hover:underline">FindHelp.org</a> · social services + legal aid by zip</li>
          </ul>
        </div>

        <div className="border-t border-sage-border pt-6 text-xs text-sage-muted sage-prose">
          Sage Legal is free forever. It ships with every bundle at no extra charge — the industry (LegalShield, Rocket Lawyer, LegalZoom) charges $39–$54/month for what Sage gives away.
        </div>
      </div>
    </div>
  );
}
